import { makeStyles } from '@material-ui/core/styles';
import { HEADER_HEIGHT, spacingSizesMap } from '@/utils/constants';

const MAX_WIDTH = {
  large: 1244,
  medium: 1044,
  small: 844,
};

const SPACING_SMALL = {
  mobile: spacingSizesMap.small * 0.7,
  desktop: spacingSizesMap.small,
};

const SPACING_MEDIUM = {
  mobile: spacingSizesMap.medium * 0.7,
  desktop: spacingSizesMap.medium,
};

const SPACING_LARGE = {
  mobile: spacingSizesMap.large * 0.7,
  desktop: spacingSizesMap.large,
};

const Z_INDEX = {
  content: 5,
  overlay: 3,
  shadow: 4,
  image: 2,
  base: 1,
};

const getVerticalSpacing = (muiTheme: any) => ({
  [muiTheme.breakpoints.down('sm')]: {
    '&.top-small': {
      '& $content, $topBackground': {
        paddingTop: SPACING_SMALL.mobile,
      },
    },

    // Default
    '&.top-medium': {
      '& $content, $topBackground': {
        paddingTop: SPACING_MEDIUM.mobile,
      },
    },

    '&.top-large': {
      '& $content, $topBackground': {
        paddingTop: SPACING_LARGE.mobile,
      },
    },

    '&.bottom-small': {
      '& $content, $bottomBackground': {
        paddingBottom: SPACING_SMALL.mobile,
      },
    },

    // Default
    '&.bottom-medium': {
      '& $content, $bottomBackground': {
        paddingBottom: SPACING_MEDIUM.mobile,
      },
    },

    '&.bottom-large': {
      '& $content, $bottomBackground': {
        paddingBottom: SPACING_LARGE.mobile,
      },
    },
  },

  [muiTheme.breakpoints.up('md')]: {
    '&.top-small-md': {
      '& $content, $topBackground': {
        paddingTop: SPACING_SMALL.desktop,
      },
    },

    '&.top-medium-md': {
      '& $content, $topBackground': {
        paddingTop: SPACING_MEDIUM.desktop,
      },
    },

    '&.top-large-md': {
      '& $content, $topBackground': {
        paddingTop: SPACING_LARGE.desktop,
      },
    },

    '&.bottom-small-md': {
      '& $content, $bottomBackground': {
        paddingBottom: SPACING_SMALL.desktop,
      },
    },

    '&.bottom-medium-md': {
      '& $content, $bottomBackground': {
        paddingBottom: SPACING_MEDIUM.desktop,
      },
    },

    '&.bottom-large-md': {
      '& $content, $bottomBackground': {
        paddingBottom: SPACING_LARGE.desktop,
      },
    },
  },
});

const getInset = (muiTheme: any) => ({
  '&.inset-hero, &.inset-quote, &.inset-video, &.inset-content-list, &.inset-featured-content, &.inset-image-gallery':
    {
      '& $bottomBackground': {
        flexGrow: 1,
      },

      '& $topBackground, $bottomBackground': {
        height: 'auto',
      },
    },

  '&.inset-hero': {
    '& $topBackground': {
      paddingBottom: 40,

      [muiTheme.breakpoints.up('md')]: {
        paddingBottom: 115,
      },
    },

    '&.offset-top': {
      '& $topBackground': {
        paddingBottom: 140,

        [muiTheme.breakpoints.up('md')]: {
          paddingBottom: 350,
        },
      },
    },
  },

  '&.inset-content-list': {
    '& $topBackground': {
      paddingBottom: 133,

      [muiTheme.breakpoints.up('md')]: {
        paddingBottom: 253,
      },
    },

    '&.offset-top': {
      '& $topBackground': {
        paddingBottom: 233,

        [muiTheme.breakpoints.up('md')]: {
          paddingBottom: 423,
        },
      },
    },
  },

  '&.inset-quote': {
    '& $topBackground': {
      paddingBottom: 30,

      [muiTheme.breakpoints.up('md')]: {
        paddingBottom: 60,
      },
    },
  },

  '&.inset-image-gallery': {
    '& $topBackground': {
      paddingBottom: 45,
    },
  },

  '&.inset-featured-content': {
    '& $topBackground': {
      paddingBottom: 160,
    },
  },

  '&.inset-video': {
    '& $topBackground': {
      paddingBottom: 100,

      [muiTheme.breakpoints.up('md')]: {
        paddingBottom: 410,
      },
    },
  },
});

const getOverlay = (muiTheme: any) => ({
  '&.left-overlay': {
    '& $overlay': {
      '&:before': {
        background: 'linear-gradient(to left, rgba(0, 0, 0, .5), rgba(0, 0, 0, 0.75))',
      },
    },

    [muiTheme.breakpoints.up('md')]: {
      '& $overlay': {
        '&:before': {
          background: 'linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75))',
        },
      },

      '&.max-width-small': {
        '& $overlay': {
          '&:before': {
            background: `linear-gradient(
            to left,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) calc(50% - ${MAX_WIDTH.small * 0.5}px),
            rgba(0, 0, 0, .75) calc(50% + ${MAX_WIDTH.small * 0.5}px),
            rgba(0, 0, 0, .75)
          )`,
          },
        },
      },

      '&.max-width-medium': {
        '& $overlay': {
          '&:before': {
            background: `linear-gradient(
            to left,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) calc(50% - ${MAX_WIDTH.medium * 0.5}px),
            rgba(0, 0, 0, .75) calc(50% + ${MAX_WIDTH.medium * 0.5}px),
            rgba(0, 0, 0, .75)
          )`,
          },
        },
      },

      '&.max-width-large': {
        '& $overlay': {
          '&:before': {
            background: `linear-gradient(
            to left,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) calc(50% - ${MAX_WIDTH.large * 0.5}px),
            rgba(0, 0, 0, .75) calc(50% + ${MAX_WIDTH.large * 0.5}px),
            rgba(0, 0, 0, .75)
          )`,
          },
        },
      },
    },
  },
  '&.right-overlay': {
    '& $overlay': {
      '&:before': {
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75))',
      },
    },

    '&.max-width-small': {
      '& $overlay': {
        '&:before': {
          background: `linear-gradient(
            to right,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) calc(50% - ${MAX_WIDTH.small * 0.5}px),
            rgba(0, 0, 0, .75) calc(50% + ${MAX_WIDTH.small * 0.5}px),
            rgba(0, 0, 0, .75)
          )`,
        },
      },
    },

    '&.max-width-medium': {
      '& $overlay': {
        '&:before': {
          background: `linear-gradient(
            to right,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) calc(50% - ${MAX_WIDTH.medium * 0.5}px),
            rgba(0, 0, 0, .75) calc(50% + ${MAX_WIDTH.medium * 0.5}px),
            rgba(0, 0, 0, .75)
          )`,
        },
      },
    },

    '&.max-width-large': {
      '& $overlay': {
        '&:before': {
          background: `linear-gradient(
            to right,
            rgba(0, 0, 0, 0),
            rgba(0, 0, 0, 0) calc(50% - ${MAX_WIDTH.large * 0.5}px),
            rgba(0, 0, 0, .75) calc(50% + ${MAX_WIDTH.large * 0.5}px),
            rgba(0, 0, 0, .75)
          )`,
        },
      },
    },
  },
  '&.center-overlay': {
    '& $overlay': {
      '&:before': {
        background: 'linear-gradient(to top, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75))',
      },
    },
  },
  '&.bottom-overlay': {
    '& $overlay': {
      '&:before': {
        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75))',
      },
    },
  },
  '&.overlay-opacity-25': {
    '& $overlay': {
      '&:after': {
        background: 'rgba(0, 0, 0, 0.25)',
      },
    },
  },
  '&.overlay-opacity-50': {
    '& $overlay': {
      '&:after': {
        background: 'rgba(0, 0, 0, 0.50)',
      },
    },
  },
  '&.overlay-opacity-75': {
    '& $overlay': {
      '&:after': {
        background: 'rgba(0, 0, 0, 0.75)',
      },
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      position: 'relative',
      width: '100%',
      ...getVerticalSpacing(muiTheme),
      ...getInset(muiTheme),
      ...getOverlay(muiTheme),

      '&.offset-top': {
        paddingTop: 100,

        [muiTheme.breakpoints.up('md')]: {
          paddingTop: 170,
        },
      },

      '&.bottom-none': {
        '& $topBackground': {
          height: '100%',
        },

        '& $bottomBackground': {
          display: 'none',
        },
      },

      '&.is-sticky': {
        display: 'flex',

        '& $backgroundContainer': {
          top: '0',
          width: '100%',
          height: '100vh',
          overflow: 'initial',
          position: 'sticky',
        },
        '& $topBackground': {
          paddingTop: 0,
        },
      },

      '&:not(.is-sticky)': {},
    },
    background: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',

      '& $topBackground, $bottomBackground': {
        height: '50%',
      },
    },
    topBackground: {
      position: 'relative',
    },
    bottomBackground: {
      position: 'relative',
    },
    backgroundContainer: {
      position: 'absolute',
      transition: 'height 250ms ease-in-out, top 250ms ease-in-out',
      width: '100%',
      height: '100%',
      top: 0,
    },
    backgroundImageContainer: {
      overflow: 'hidden',
    },
    backgroundImage: {
      position: 'relative',

      '&.is-multiple': {
        position: 'absolute',
        height: '100%',
        width: '100%',
        top: '0',
        transition: 'opacity 250ms linear',
      },

      '&.is-visible': {
        opacity: 1,
        zIndex: Z_INDEX.image,
      },

      '&.is-hidden': {
        opacity: 0,
        zIndex: Z_INDEX.base,
        transitionDelay: '250ms',
      },
    },
    backgroundVideoContainer: {},
    backgroundVideo: {},
    overlay: {
      position: 'absolute',
      left: 0,
      width: '100%',
      top: 0,
      height: '100%',

      '&:after, &:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        transition: 'all 300ms ease-in',
      },

      '&:before': {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        zIndex: Z_INDEX.shadow,
      },

      '&:after': {
        top: 0,
        zIndex: Z_INDEX.overlay,
      },
    },
    content: {
      position: 'relative',
      zIndex: Z_INDEX.content,
      display: 'flex',
      width: '100%',
    },
  }),
  { name: 'GenContentContainer' },
);

export default useStyles;
